import CompanyLogo from '../company/CompanyLogo';
import UserBadge from '../profile/UserBadge';
import LanguageSelector from '../languages/LanguageSelector';
import useScreenType from '../../hooks/utility/useScreenType';
import CompanyDetailsModal from '../../modals/CompanyDetailsModal';
import { useMemo, useState } from 'react';
import useInitialData from '../../hooks/global/useInitialData';
import { Link } from 'react-router-dom';
import useRouter from '../../hooks/navigation/useRouter';
import useMenu from '../../hooks/menu/useMenu';
import { collect } from 'collect.js';
import { Drawer } from '@mui/material';
import classNames from 'classnames';
import SideBarIcon from '../../assets/logo/SideBarIcon';
import useTheme from '../../hooks/ui/useTheme';
import BackIcon from '../../assets/logo/BackIcon';
import { useTranslation } from 'react-i18next';
import { SCREENS } from '../../services/exports/Constants';
import useRouteMatch from '../../hooks/navigation/useRouteMatch';
import useAppMethods from '../../hooks/utility/useAppMethods';
import useStorefront from '../../hooks/storefront/useStorefront';
import { Fade as Hamburger } from 'hamburger-react';
import CircleArrow from '../../assets/icons/CircleArrow';
import useStorefrontRouter from '../../modules/storefront/hooks/useStorefrontRouter';
import { StorefrontPages } from '../../modules/storefront/exports/Enums';
import OrderBasketButton from '../order-basket/OrderBasketButton';
import usePageHeader from '../../hooks/navigation/usePageHeader';

type NavItemType = 'link' | 'button';

type WindowOrigin = 'storefront' | 'shop';

interface INavItem {
  label: string;
  to?: string;
  type: NavItemType;
  origin?: WindowOrigin;
  onClick?: () => void;
  onClicked?: () => void;
  className?: string;
  textClassName?: string;
  style?: React.CSSProperties;
  iconRight?: React.ReactNode;
}

interface IDevice {
  items: INavItem[];
}

interface IBack {
  onClick: () => void;
  title: string;
}

const PageHeader = (): JSX.Element => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Headers:PageHeader' });
  const { data } = useInitialData();
  const { company, franchise } = data;
  const { isDesktop } = useScreenType();
  const { generatePath } = useRouter();
  const { allCategories, generateMenuScrollQuery } = useMenu();
  const routeMatch = useRouteMatch();
  const { isStorefront } = useStorefront();
  const [showCompanyDetailsModal, setShowCompanyDetailsModal] = useState<boolean>(false);
  const { toggleOrderMethodModal } = useAppMethods();

  const items = [
    {
      label: t('nav_items.menu'),
      to: `${generatePath(SCREENS.HOME_SCREEN)}${generateMenuScrollQuery(
        collect(allCategories).first()?.id,
        routeMatch(SCREENS.HOME_SCREEN) ? 'smooth' : 'auto',
      )}`,
      type: 'link',
    },
    franchise?.has_multiple_companies && {
      label: t('nav_items.locations'),
      type: 'button',
      onClick: () => toggleOrderMethodModal({ openModal: true }),
    },
    {
      label: t('nav_items.store_info'),
      to: '/store-info',
      type: 'button',
      onClick: () => setShowCompanyDetailsModal(true),
    },
  ].filter(Boolean) as INavItem[];

  const renderCompanyDetailsModal = useMemo<JSX.Element>(() => {
    return (
      <CompanyDetailsModal
        data={company}
        openModal={showCompanyDetailsModal}
        toggleModal={() => setShowCompanyDetailsModal(false)}
      />
    );
  }, [company, showCompanyDetailsModal]);

  const renderContent = (): JSX.Element => {
    const config = {
      items,
    };
    if (isStorefront) {
      return <PageHeader.Storefront {...config} />;
    }
    return isDesktop ? <PageHeader.Desktop {...config} /> : <PageHeader.Mobile {...config} />;
  };

  return (
    <>
      {renderContent()}
      {renderCompanyDetailsModal}
    </>
  );
};

export default PageHeader;

PageHeader.NavigationLink = ({
  to,
  label,
  type,
  className,
  textClassName,
  style,
  iconRight,
  origin,
  onClick,
  onClicked,
}: INavItem): JSX.Element => {
  const { navigate } = useStorefrontRouter();
  if (type === 'button') {
    return (
      <button
        onClick={() => {
          onClick();
          onClicked?.();
        }}
        className={classNames('text-left shrink-0', className)}
        style={style}
      >
        <p className={classNames('text-sm text-brand-text-secondary hover:underline', textClassName)}>{label}</p>
      </button>
    );
  }

  if (origin === 'storefront') {
    return (
      <button className={classNames('shrink-0', className)} onClick={() => navigate(to)} style={style}>
        <p className={classNames('text-sm text-brand-text-secondary hover:underline', textClassName)}>{label}</p>
        {iconRight}
      </button>
    );
  }
  return (
    <Link
      to={to}
      viewTransition
      className={classNames('shrink-0', className)}
      onClick={() => onClicked?.()}
      style={style}
    >
      <p className={classNames('text-sm text-brand-text-secondary hover:underline', textClassName)}>{label}</p>
      {iconRight}
    </Link>
  );
};

PageHeader.Desktop = ({ items }: IDevice): JSX.Element => {
  const { navigate } = useRouter();
  const { isDesktop } = useScreenType();
  const { headerHeight } = usePageHeader();
  return (
    <div
      className="bg-brand-background-primary layout-container dark:shadow-none"
      style={{ height: isDesktop ? headerHeight.desktop : undefined }}
    >
      <div className="flex lg:px-none px-md py-sm justify-between items-center">
        <div className="cursor-pointer" onClick={() => navigate({ web: { to: SCREENS.HOME_SCREEN } })}>
          <CompanyLogo />
        </div>

        <div className="flex items-center gap-x-lg">
          <div className="flex items-center gap-x-lg">
            {items.map((item, index) => (
              <PageHeader.NavigationLink key={index} {...item} />
            ))}
          </div>
          <div className="flex items-center gap-x-lg">
            <LanguageSelector />
            <UserBadge />
            <OrderBasketButton />
          </div>
        </div>
      </div>
    </div>
  );
};

PageHeader.Mobile = ({ items, children }: IDevice & { children?: React.ReactNode }): JSX.Element => {
  const theme = useTheme();
  const { navigate } = useRouter();
  const [open, setOpen] = useState<boolean>(false);

  const toggleSideBar = (): void => {
    setOpen(!open);
  };

  return (
    <div className="flex px-sm py-sm justify-between items-center gap-2 h-[60px] bg-brand-background-primary shadow-section dark:shadow-none">
      <div className="flex items-center gap-x-sm">
        <button onClick={() => setOpen(true)}>
          <SideBarIcon className="h-[16px] w-[16px] stroke-gray-700" color={theme.color.mobile.tertiary} />
        </button>
        <div className="cursor-pointer" onClick={() => navigate({ web: { to: SCREENS.HOME_SCREEN } })}>
          <CompanyLogo className="h-[32px]" />
        </div>
      </div>
      <div className="shrink-0">
        <UserBadge />
      </div>
      <Drawer
        anchor={'left'}
        open={open}
        onClose={toggleSideBar}
        sx={{
          '& .MuiDrawer-paper': {
            width: '80vw',
            backgroundColor: theme.color.brand.background.primary,
          },
          backdrop: {
            sx: {
              backdropFilter: 'blur(8px)',
              backgroundColor: 'rgba(52, 52, 52, 0.5)',
            },
          },
        }}
        ModalProps={{
          disableEnforceFocus: true,
        }}
      >
        <div className="flex flex-col [&>*:not(:last-child)]:border-b [&>*:not(:last-child)]:border-brand-border">
          {children ??
            items.map((item, index) => (
              <PageHeader.NavigationLink key={index} {...item} onClicked={toggleSideBar} className="p-sm w-full" />
            ))}
          <div className="p-sm w-full">
            <LanguageSelector />
          </div>
        </div>
      </Drawer>
    </div>
  );
};

PageHeader.Back = ({ onClick, title }: IBack): JSX.Element => {
  const theme = useTheme();
  return (
    <button
      onClick={onClick}
      className="flex items-center w-full p-md text-sm z-40 bg-brand-background-primary text-brand-text-secondary lg:p-none lg:w-fit shadow-section dark:shadow-none"
    >
      <BackIcon
        className="h-3 !w-3 mr-xs"
        color={theme.isDarkMode ? theme.color.brand.icon.color : theme.color.mobile.tertiary}
      />
      {title}
    </button>
  );
};

PageHeader.Storefront = ({ items }: IDevice): JSX.Element => {
  const { store } = useStorefront();
  const { navigate } = useStorefrontRouter();
  const theme = useTheme();
  const { isDesktop } = useScreenType();
  const [open, setOpen] = useState<boolean>(false);

  const toggleSideBar = (): void => {
    setOpen(!open);
  };

  const onlineShopItems = useMemo(
    () =>
      collect(items)
        .prepend({
          label: 'Home',
          to: StorefrontPages.Home,
          type: 'link',
          origin: 'storefront',
          iconRight: <CircleArrow className="h-4 w-4 -rotate-90" color={theme.color.brand.text.secondary} />,
        })
        .unique('label')
        .toArray() as INavItem[],
    [theme],
  );

  const storefrontItems = useMemo(
    () =>
      collect(store?.navigation_items)
        .where('page.identifier', '!=', 'menu')
        .where('is_hidden', false)
        .map((item) => ({
          label: item.name,
          to: item.page.href,
          type: 'link',
          origin: 'storefront',
        }))
        .unique('label')
        .map((item) => ({
          ...item,
          iconRight: <CircleArrow className="h-5 w-5 -rotate-90" color={theme.color.brand.text.secondary} />,
        }))
        .toArray() as INavItem[],
    [store?.navigation_items, theme],
  );

  enum NavGroupType {
    OnlineShop = 'online_shop',
    Website = 'website',
  }

  const navGroups = [
    {
      type: NavGroupType.OnlineShop,
      label: 'Shop',
      items,
    },
    {
      type: NavGroupType.Website,
      label: 'Website',
      items: storefrontItems,
    },
  ];

  const sideBar = useMemo(() => {
    return (
      <Drawer
        anchor={'left'}
        open={open}
        onClose={toggleSideBar}
        sx={{
          '& .MuiDrawer-paper': {
            width: isDesktop ? 400 : '80vw',
            backgroundImage: 'none',
            backgroundColor: theme.color.brand.background.primary,
          },
        }}
        ModalProps={{
          BackdropProps: theme.isDarkMode
            ? {
                sx: {
                  backdropFilter: 'blur(4px)',
                  backgroundColor: 'rgba(52, 52, 52, 0.5)',
                },
              }
            : {
                sx: {
                  backdropFilter: 'blur(4px)',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                },
              },
          disableEnforceFocus: true,
        }}
      >
        <div className="flex flex-col">
          {navGroups.map((group, index) => (
            <div key={index}>
              <div className="flex flex-col [&>*:not(:last-child)]:border-b [&>*:not(:last-child)]:border-brand-border">
                {group.items.map((item, index) => (
                  <PageHeader.NavigationLink
                    key={index}
                    {...item}
                    onClicked={toggleSideBar}
                    className="p-md w-full dark:hover:bg-black-600 hover:bg-gray-200 transition-all duration-300 border-b border-brand-border flex justify-between items-center"
                    textClassName="lg:!text-md !text-sm"
                  />
                ))}
              </div>
            </div>
          ))}
          <div className="p-sm w-full">
            <LanguageSelector />
          </div>
        </div>
      </Drawer>
    );
  }, [navGroups, open, theme, isDesktop]);

  const renderHeader = (): JSX.Element => {
    if (!isDesktop) {
      return (
        <>
          <div className="flex px-sm py-sm justify-between items-center h-[60px] bg-brand-background-primary shadow-section dark:shadow-none">
            <div className="flex items-center gap-x-sm">
              <button onClick={() => setOpen(true)}>
                <SideBarIcon className="h-[16px] w-[16px] stroke-gray-700" color={theme.color.mobile.tertiary} />
              </button>
              <div className="cursor-pointer" onClick={() => navigate(StorefrontPages.Home)}>
                <CompanyLogo className="h-[32px]" />
              </div>
            </div>
            <UserBadge />
          </div>
          {sideBar}
        </>
      );
    }

    return (
      <div className="bg-brand-background-primary layout-container flex items-center gap-x-sm relative">
        <button onClick={() => setOpen(!open)}>
          <Hamburger toggled={open} size={16} color={theme.color.brand.text.secondary} />
        </button>
        <div className="flex items-center justify-between lg:px-none px-md py-sm  gap-x-lg w-full">
          <div className="flex items-center gap-x-lg">
            {onlineShopItems.map((item, index) => (
              <PageHeader.NavigationLink key={index} {...item} className="flex gap-2 items-center" />
            ))}
          </div>
          <div
            className="cursor-pointer absolute left-1/2 -translate-x-1/2"
            onClick={() => navigate(StorefrontPages.Home)}
          >
            <CompanyLogo />
          </div>
          <div className="flex items-center gap-x-lg">
            <LanguageSelector />
            <UserBadge />
            <OrderBasketButton />
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {renderHeader()}
      {sideBar}
    </>
  );
};
